<template>
  <div :class="'tw-w-full ' + $attrs.class">
    <select 
      class="tw-block tw-py-4 tw-w-full tw-font-bold tw-appearance-none focus:tw-outline-none tw-border-gray-400 focus:tw-border-orange-medium focus:tw-ring-0 tw-bg-transparent tw-text-2xl" 
      @change="handleChange"
      :name="props.id" 
      :id="props.id">
      <option :value="-1" default disabled :selected="selectedTime === -1">Pick a Time</option>
      <option 
        v-for="(time, index) in times" 
        :key="'time-'+time.hours.toString()+time.minutes.toString()" 
        :selected="selectedTime === index"
        :value="index">
          {{time.hours > 12 ? time.hours - 12 : time.hours}}:{{time.minutes === 0 ? '00' : time.minutes}}
          <template v-if="time.hours < 12">am</template>
          <template v-else>pm</template>
      </option>
    </select>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed, watch } from "vue";
  import type {PropType} from "vue";
  import { DateTime, Duration} from "luxon";

  const emit = defineEmits(['onChange'])

  const times = ref([] as Duration[])
  const selectedTime = ref(-1)
   
  const props = defineProps({
    hours: {
      type: Number,
      default: 0,
    },
    minutes: {
      type: Number,
      default: 0,
    },
    minTime: {
      type: Date,
      default: "",
    },
    maxTime: {
      type: Date,
      default: "",
    },
    selectedTime: {
      type: (Object as PropType<Duration>),
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
  });

  onMounted(() => {
    setSelectableTimes();
    // Preset the selected Time
    if (props.selectedTime) {
      if (times.value) {
        let idx = times.value.findIndex((dur) => {
          return dur.hours == props.selectedTime.hours && dur.minutes == props.selectedTime.minutes
        })
        selectedTime.value = idx
      } else {
        selectedTime.value = -1
      }
    }
  })

  function setSelectableTimes() {
    const start = DateTime.fromJSDate(props.minTime);
    const end = DateTime.fromJSDate(props.maxTime);
    const timeDiff = end.diff(start, ['hours', 'minutes']).toObject() //=> { hours: 1, minutes: 2 }

    const fullHours = timeDiff.hours;
    const beginningHour = parseFloat(start.toFormat('H'));
    const beginningMinute = parseFloat(start.toFormat('m'));

    const selectableTimes = [] as Duration[];
    // Loop over full hours to add their selectable times

    for (let i = beginningHour; i <= beginningHour+fullHours!; i++) {
      if (i == beginningHour) {
        if (beginningMinute > 45) {
          // skip
        } else if (beginningMinute > 30) {
          selectableTimes.push({hours: i, minutes: 45} as Duration)
        } else if (beginningMinute > 15) {
          selectableTimes.push({hours: i, minutes: 30} as Duration)
          selectableTimes.push({hours: i, minutes: 45} as Duration)
        } else if (beginningMinute > 0) {
          selectableTimes.push({hours: i, minutes: 15} as Duration)
          selectableTimes.push({hours: i, minutes: 30} as Duration)
          selectableTimes.push({hours: i, minutes: 45} as Duration)
        } else {
          // its the 0 minute!
          selectableTimes.push({hours: i, minutes: 0} as Duration)
          selectableTimes.push({hours: i, minutes: 15} as Duration)
          selectableTimes.push({hours: i, minutes: 30} as Duration)
          selectableTimes.push({hours: i, minutes: 45} as Duration)
        }
      } else {
        selectableTimes.push({hours: i, minutes: 0} as Duration)
        selectableTimes.push({hours: i, minutes: 15} as Duration)
        selectableTimes.push({hours: i, minutes: 30} as Duration)
        selectableTimes.push({hours: i, minutes: 45} as Duration)
      }
    }

    times.value = selectableTimes;
  }

  watch(() => props.minTime, (newVal, oldVal) => { 
    setSelectableTimes();
  })

  watch(props, async () => {
    if (times.value && props.selectedTime) {
      let idx = times.value.findIndex((dur) => {
        return dur.hours == props.selectedTime.hours && dur.minutes == props.selectedTime.minutes
      })
      selectedTime.value = idx
    } else {
      selectedTime.value = -1
    }
  })

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const time = times.value[parseFloat(target.value)];
  emit("onChange", time);
};


</script>